import {
  FDAvatar,
  FDPaper, FDSecondaryActionIconButton,
  FDStack,
} from '@facet-wealth/design';
import React from 'react';
import { dataAttrs } from 'Helpers/Testing';
import fr from 'facet-rest';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Spacer from 'Components/UI/Spacer';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import { useCustomInsightUpdated } from 'Hooks/useRefreshEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';

const useStyles = buildUseStyles('PlannerInsightChat', ((theme) => ({
  container: {
    position: 'relative', // The positioning context for the absolutely positioned avatar
    '& div.FD-MuiPaper-root': { // target the paper
      borderRadius: `0 ${theme.radius.md}px ${theme.radius.md}px ${theme.radius.md}px`, // remove the border radius on the top left corner
    },
  },
  avatarOnTopLeft: {
    position: 'absolute', // Takes the avatar out of the document flow and positions it
    top: -16, // Adjust this value to control how far up the avatar is
    left: 20, // Adjust this value to control the horizontal position
    zIndex: 1, // Ensures the avatar is on top of the paper
    '& .FD-MuiAvatar-root': { // target the avatar
      border: `6px solid ${theme.palette.facet.lightGray}`, // bone background border around the avatar
    },
    width: '100%',
  },
  editOnTopRight: {
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(1),
  },
})));

export const PlannerInsightChatLayout = () => {
  const { resource } = useMetaLayoutContext();
  const { loadModalCollectionByLink } = useModalContext();
  const { refreshPageContentCollection } = usePageContentContext();

  const classes = useStyles();

  useCustomInsightUpdated(async () => {
    await refreshPageContentCollection();
  }, []);

  if (!resource) {
    return null;
  }

  const PlannerAvatar = resource.withRelationship(
    fr.relationships.plannerProfileImage.name,
    (rel) => <FDAvatar src={rel.link('related')?.url()} alt={rel.meta('altText')} size={'lg'} />,
    () => null,
  );

  const PlannerEdit = resource.withRelationship(
    fr.relationships.editInsight.name,
    (rel) => (
      <div className={classes.editOnTopRight}>
        <FDSecondaryActionIconButton
          onClick={() => loadModalCollectionByLink(rel.link('related').url())}
          icon={'edit'}
          {...dataAttrs('planner-insight-edit-button')}
        />
      </div>
    ),
    () => null,
  );

  if (!resource.attr('cfpCustomInsightPlannerDisplayName')) {
    return null;
  }

  return resource.withRelationship(
    fr.relationships.guidedHouseholdReviewLayout.name, () => null,
    () => (
      <div className={classes.container} {...dataAttrs('planner-insight')}>
        <div className={classes.avatarOnTopLeft}>
          <FDStack direction={'row'} spacing={1}>
            {PlannerAvatar}
            <FDStack alignItems={'flex-end'} direction={'row'} justifyContent={'space-between'} width={'100%'}>
              <MetaControlField attr={'cfpCustomInsightPlannerDisplayName'} />
              {PlannerEdit}
            </FDStack>
          </FDStack>
        </div>
        <FDPaper appearance={'flat'} color={'info'} padding={500} {...dataAttrs('roadmap-executive-summary-planner-insight-deliverable-section')}>
          <FDStack direction={'column'} spacing={5}>
            <Spacer />
            <MetaControlField attr={'cfpCustomInsightContent'} />
          </FDStack>
        </FDPaper>
      </div>
    ),
  );
};
